/**
 * Drop Generator data layer. All endpoints ride the { success, data }
 * envelope, so every queryFn reads res.data.data; one invalidate closure
 * over the root key serves every mutation (list-groups pattern). The Live
 * tab's reads live in hooks/useLiveQueries, the DEV simulation in
 * simulation/useSimulation.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/services/api';

import type { DropGenCreativeFillStatus, DropGenSchedulerStatus } from '@trafficpulse/shared';

import type {
  DayPlanPreview,
  DropGenContainerDetail,
  DropGenPlanDetail,
  DropGenPlanRow,
} from './drop-generator.types';

interface Envelope<T> {
  data: T;
}

const BASE = '/drop-generator';

export const dropGeneratorKeys = {
  all: ['drop-generator'] as const,
  plans: () => [...dropGeneratorKeys.all, 'plans'] as const,
  plan: (countryKey: string) =>
    [...dropGeneratorKeys.all, 'plan', countryKey] as const,
  container: (id: string) =>
    [...dropGeneratorKeys.all, 'container', id] as const,
  preview: (countryKey: string, at: string | null = null) =>
    [...dropGeneratorKeys.all, 'preview', countryKey, at] as const,
  creativeFill: (countryKey: string) =>
    [...dropGeneratorKeys.all, 'creative-fill', countryKey] as const,
  events: (countryKey: string) =>
    [...dropGeneratorKeys.all, 'events', countryKey] as const,
  verdicts: (countryKey: string) =>
    [...dropGeneratorKeys.all, 'verdicts', countryKey] as const,
  scheduler: () => [...dropGeneratorKeys.all, 'scheduler'] as const,
};

export function usePlans() {
  return useQuery({
    queryKey: dropGeneratorKeys.plans(),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenPlanRow[]>>(`${BASE}/plans`);
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

export function usePlan(countryKey: string | null) {
  return useQuery({
    queryKey: dropGeneratorKeys.plan(countryKey ?? 'none'),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenPlanDetail>>(
        `${BASE}/plans/${countryKey}`,
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey),
    staleTime: 15_000,
    retry: false,
  });
}

export function useContainerDetail(id: string | null) {
  return useQuery({
    queryKey: dropGeneratorKeys.container(id ?? 'none'),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenContainerDetail>>(
        `${BASE}/containers/${id}`,
      );
      return res.data.data;
    },
    enabled: Boolean(id),
    staleTime: 0,
    gcTime: 0,
  });
}

/** The planner's zero-write dry run for today. Any config mutation
 *  invalidates it (all mutations invalidate the root key). The Live tab
 *  passes poll=true so the cards track sends the FEED already shows —
 *  15s, not the feed's 5s, because this endpoint runs a full dry run. */
export function useDayPreview(
  countryKey: string | null,
  poll = false,
  at: string | null = null,
) {
  return useQuery({
    queryKey: dropGeneratorKeys.preview(countryKey ?? 'none', at),
    queryFn: async () => {
      const res = await api.get<Envelope<DayPlanPreview>>(
        `${BASE}/planner/${countryKey}/preview`,
        { params: at ? { at } : undefined },
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey),
    refetchInterval: poll ? 15_000 : false,
    staleTime: 0,
    retry: false,
  });
}

/** Creative-fill progress — polls while FILLING, stops in terminal states.
 *  Background polling stays on (occluded windows must not freeze a live
 *  progress line — the match-counts lesson). 404 (no runs yet) = idle. */
export function useCreativeFillStatus(countryKey: string | null) {
  return useQuery({
    queryKey: dropGeneratorKeys.creativeFill(countryKey ?? 'none'),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenCreativeFillStatus>>(
        `${BASE}/generate/${countryKey}/creative/status`,
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey),
    refetchInterval: (q) =>
      q.state.data?.status === 'FILLING' ? 2_500 : false,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: false,
  });
}

/** The Phase 8 heartbeat + every plan's state; polls while a country page is open. */
export function useSchedulerStatus(enabled = true) {
  return useQuery({
    queryKey: dropGeneratorKeys.scheduler(),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenSchedulerStatus>>(`${BASE}/scheduler/status`);
      return res.data.data;
    },
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
